import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps, Theme } from "@mui/material/styles";
import type { MouseEvent } from "react";
import { Link as RouterLink, type To } from "react-router";
import type WorkVersion from "work/work-agreements/versions/work-version";

/**
 * The version of the work beside its project name -- "v2", "v3 (proposed)" -- so two records with
 * one name never sit side by side looking alike (#6636).
 *
 * A version a later one replaced keeps the same small badge, drawn quieter with a dashed edge, and
 * says when it was superseded in a tooltip. Written into the badge, "v1 · superseded on
 * 10/13/2026" was too long to sit beside the name, so the column wrapped it onto a line of its own.
 * The tooltip describes the badge rather than naming it (`describeChild`), so the badge still
 * reads "v1" and the date is its description.
 *
 * Given `to`, the badge is a link there: the Work list's badge opens the work's earlier versions
 * (#7090). It sits in a row that opens the record, so a click on it opens only the link.
 *
 * Read the version with `WorkVersion.of(workAgreement)` -- a proposal's is
 * `WorkVersion.of(proposal.workAgreement)` -- and draws nothing when there is none.
 */
export default function WorkVersionChip(props: Readonly<{
  version: WorkVersion | undefined;
  /** Where the badge links to, when it is a link. */
  to?: To;
  sx?: SxProps<Theme>;
}>) {
  const { version, to } = props;
  if (!version) return null;
  const superseded = version.supersededText;
  // Without a destination it is read as part of the name it follows, not as a control.
  const as = to === undefined
    ? { component: "span" as const }
    : {
        component: RouterLink,
        to,
        clickable: true,
        "aria-label": `${version.label}: show earlier versions`,
        onClick: (event: MouseEvent) => event.stopPropagation(),
      };
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      label={version.label}
      {...as}
      data-testid="work-version-chip"
      data-superseded={superseded ? "true" : undefined}
      sx={[
        { marginLeft: 1, height: 20, fontSize: "0.75rem", fontWeight: "normal", verticalAlign: "middle", flexShrink: 0 },
        superseded ? { borderStyle: "dashed", color: "text.secondary" } : {},
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
  if (!superseded) return chip;
  return (
    <Tooltip title={`${version.label} was ${superseded}`} describeChild>
      {chip}
    </Tooltip>
  );
}
